/**
 * お気に入りの端末（自分のチームの気球・チェイスカーなど）。
 *
 * 共有サーバーには複数チームの端末がつながるため、航跡が多数並んで見づらい
 * （利用者の要望、2026-10-09、docs/08 §192）。お気に入りは端末に覚えておき:
 * - 一覧の先頭に出す
 * - 「お気に入り以外」をまとめて表示・非表示にできる
 *
 * 表示・非表示の決まり（`isTerminalShown`）:
 * - お気に入り: 個別に隠していなければ表示
 * - お気に入り以外: 「お気に入り以外を表示」中は個別に隠していなければ表示、
 *   「お気に入り以外を隠す」中は個別に出したものだけ表示
 *
 * お気に入りと「お気に入り以外を表示するか」は保存する（次に開いても同じ）。
 * 個別の表示・非表示（一覧のチェック）はこれまでどおりその場限り。
 */

export interface TerminalVisibility {
  /** お気に入りの端末ID（保存する） */
  favorites: Set<string>;
  /** お気に入り以外を表示するか（保存する） */
  showNonFavorites: boolean;
  /** 一覧のチェックで個別に隠した端末（その場限り） */
  hidden: Set<string>;
  /** お気に入り以外を隠している間に、一覧のチェックで個別に出した端末（その場限り） */
  revealed: Set<string>;
}

export function isTerminalShown(v: TerminalVisibility, id: string): boolean {
  if (v.favorites.has(id) || v.showNonFavorites) return !v.hidden.has(id);
  return v.revealed.has(id);
}

/** 一覧のチェックで1台だけ表示・非表示を切り替える */
export function setTerminalShown(v: TerminalVisibility, id: string, shown: boolean): void {
  if (shown) {
    v.hidden.delete(id);
    v.revealed.add(id);
  } else {
    v.hidden.add(id);
    v.revealed.delete(id);
  }
}

/**
 * 「お気に入り以外」をまとめて表示・非表示にする。お気に入り以外に対する
 * 個別の切り替えはここで捨てる（押した結果が全台に揃うように）
 */
export function setShowNonFavorites(v: TerminalVisibility, show: boolean): void {
  v.showNonFavorites = show;
  for (const id of [...v.hidden]) if (!v.favorites.has(id)) v.hidden.delete(id);
  v.revealed.clear();
}

/** お気に入りを先頭に、それぞれ端末ID順に並べる（元の配列は変えない） */
export function sortFavoritesFirst<T extends { terminalId: string }>(list: T[], favorites: Set<string>): T[] {
  return [...list].sort((a, b) => {
    const fa = favorites.has(a.terminalId) ? 0 : 1;
    const fb = favorites.has(b.terminalId) ? 0 : 1;
    return fa - fb || a.terminalId.localeCompare(b.terminalId);
  });
}

/**
 * 入力された端末IDを、受信中・既知の端末IDに合わせる。大文字小文字だけ違う
 * ものがあればそちらを採る（"kaze"と打っても受信中の"Kaze"に付く）。
 * 前後の空白は除く。空なら null
 */
export function resolveTerminalIdInput(raw: string, knownIds: Iterable<string>): string | null {
  const id = raw.trim();
  if (!id) return null;
  const known = [...knownIds];
  if (known.includes(id)) return id;
  return known.find((k) => k.toLowerCase() === id.toLowerCase()) ?? id;
}

const FAVORITES_KEY = 'gv4d-favorite-terminals';
const SHOW_NON_FAVORITES_KEY = 'gv4d-show-non-favorites';

export function loadFavorites(): Set<string> {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

export function saveFavorites(favorites: Set<string>): void {
  try {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify([...favorites].sort()));
  } catch {
    // 保存できなくても今回のセッション中は効く
  }
}

export function loadShowNonFavorites(): boolean {
  try {
    return localStorage.getItem(SHOW_NON_FAVORITES_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function saveShowNonFavorites(show: boolean): void {
  try {
    localStorage.setItem(SHOW_NON_FAVORITES_KEY, String(show));
  } catch {
    // 保存できなくても今回のセッション中は効く
  }
}
